import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import {
    Button,
    DatePicker,
    Drawer,
    Form,
    InputNumber,
    message,
    Select,
    Space
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import {
    GET_CONSOLIDATION_PREVIEW_STATISTICS,
    GetConsolidationPreviewResponse,
    GetConsolidationPreviewVariables
} from '../graphql/queries/getConsolidationPreview';
import {
    GET_HUB_AND_WHS_OPTIONS,
    GetHubAndWhsOptionsResponse
} from '../graphql/queries/getHubAndWhsOptions';
import {
    GET_CARRIER_OPTIONS,
    GetCarrierOptionsResponse
} from '../graphql/queries/getCarrierOptions';
import {
    BagType,
    getBagTypeOptions
} from '../graphql/types/ConsolidationTypes';
import { useTranslation } from 'react-i18next';

interface CreateConsolidationTaskDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (taskData: CreateTaskData) => void;
    isExecuting?: boolean;
    pageType?: 'swiftx' | 'other-carrier'; // 页面类型：swiftx 或 other-carrier
}

interface CreateTaskData {
    destinationHubId: string;
    destinationWhsId: string | null; // 允许为null，表示外地包裹或All
    startTime: string | null; // 允许为null
    endTime: string | null; // 允许为null
    estimatedParcelCount: number;
    estimatedBagCount: number;
    bagType: BagType; // 包牌段数
    carrierCode?: number; // Carrier代码，仅用于other-carrier页面
    isAllCarriers?: boolean; // 是否选择了ALL Carriers
    carrierTasks?: Array<{
        carrierCode: number;
        estimatedParcelCount: number;
        estimatedBagCount: number;
    }>; // 当选择ALL时，各个Carrier的任务数据
}

const CreateConsolidationTaskDrawer: React.FC<CreateConsolidationTaskDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    isExecuting = false,
    pageType = 'swiftx'
}) => {
    const [form] = Form.useForm();
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState<GetConsolidationPreviewResponse['getConsolidationPreviewStatistics'] | null>(null);
    const [selectedHubId, setSelectedHubId] = useState<string>('');
    const [timeRange, setTimeRange] = useState<[any, any] | null>(null);
    const { t } = useTranslation();
    const [selectedCarrier, setSelectedCarrier] = useState<number | 'ALL'>('ALL'); // 新增状态用于追踪选择的Carrier
    const [carrierPreviewMap, setCarrierPreviewMap] = useState<Map<number, GetConsolidationPreviewResponse['getConsolidationPreviewStatistics']>>(new Map()); // 存储各个Carrier的预览数据

    // 获取可用的Hub和WHS选项
    const { data: hubWhsData, loading: hubWhsLoading } = useQuery<GetHubAndWhsOptionsResponse>(
        GET_HUB_AND_WHS_OPTIONS
    );

    // 获取Carrier选项（仅用于other-carrier页面）
    const { data: carrierData, loading: carrierLoading } = useQuery<GetCarrierOptionsResponse>(
        GET_CARRIER_OPTIONS,
        {
            skip: pageType !== 'other-carrier', // 只在other-carrier页面时查询
            variables: {
                includeSwiftX: false // other-carrier页面不需要包含SwiftX
            }
        }
    );

    // 获取当前用户信息
    const { currentWarehouse } = useWarehouseContext();
    const currentHubId = currentWarehouse?.id?.toString() || '';

    // 预览统计查询
    const [loadPreview, { loading: previewLoading }] = useLazyQuery<
        GetConsolidationPreviewResponse,
        GetConsolidationPreviewVariables
    >(GET_CONSOLIDATION_PREVIEW_STATISTICS, {
        onCompleted: (data: GetConsolidationPreviewResponse) => {
            setPreviewData(data.getConsolidationPreviewStatistics);
        },
        onError: (error: any) => {
            message.error(`${t(error.message)}`);
            setPreviewData(null);
        }
    });

    const hubOptions = hubWhsData?.getHubAndWhsOptions.hubOptions.map(hub => ({
        value: hub.hubId,
        label: hub.hubName
    })) || [];

    const whsOptions = hubWhsData?.getHubAndWhsOptions.whsOptions.map(whs => ({
        value: whs.whsId,
        label: whs.whsName,
        parentHubId: whs.parentHubId
    })) || [];

    // 构建Carrier选项，在Other Carrier页面时添加ALL选项
    const carrierOptions = useMemo(() => {
        const options = carrierData?.getCarrierOptions.map(carrier => ({
            value: carrier.value,
            label: carrier.name
        })) || [];

        // 仅在other-carrier页面添加ALL选项
        if (pageType === 'other-carrier') {
            return [
                { value: 'ALL', label: 'ALL' },
                ...options
            ];
        }

        return options;
    }, [carrierData, pageType]);

    console.log('carrierData:', carrierData);
    console.log('carrierOptions:', carrierOptions);

    // WHS选项过滤逻辑
    const filteredWhsOptions = useMemo(() => {
        if (!selectedHubId) return [];
        
        const hubWhs = whsOptions.filter(whs => whs.parentHubId === selectedHubId);
        
        // 如果选择的Hub是当前Hub，可以选择具体WHS或All
        return [
            { value: 'ALL', label: 'ALL WHS' },
            ...hubWhs.map(whs => ({
                value: whs.value,
                label: whs.label
            }))
        ];
    }, [selectedHubId, currentHubId, whsOptions]);

    // 包牌段数选项过滤逻辑
    const filteredBagTypeOptions = useMemo(() => {
        if (pageType === 'swiftx') {
            // SWIFTX页面：如果选择的Hub是当前Hub，不显示ONE_SEGMENT
            if (selectedHubId && selectedHubId == currentHubId) {
                return getBagTypeOptions().filter(option => option.value !== BagType.ONE_SEGMENT);
            }
            // 其他情况可以选择所有包牌段数
            return getBagTypeOptions();
        } else {
            // Other Carrier页面：不显示Bag Type选项，但默认选中ONE_SEGMENT
            return [];
        }
    }, [selectedHubId, currentHubId, pageType]);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setPreviewData(null);
            setSelectedHubId('');
            setTimeRange(null);
            setCarrierPreviewMap(new Map());

            // 对于other-carrier页面，设置默认选中ALL
            if (pageType === 'other-carrier') {
                setSelectedCarrier('ALL');
                form.setFieldsValue({ carrierCode: 'ALL' });
            }
        }
    }, [visible, form, pageType]);

    // 处理Hub选择变化
    const handleHubChange = (hubId: string) => {
        setSelectedHubId(hubId);
        // 清空WHS选择和包牌段数选择
        form.setFieldsValue({
            destinationWhsId: undefined,
            bagType: undefined
        });
    };

    const handlePreview = async () => {
        // 根据页面类型验证不同的字段
        const fieldsToValidate = pageType === 'swiftx'
            ? ['destinationHubId', 'destinationWhsId', 'bagType']
            : ['destinationHubId', 'carrierCode'];

        try {
            const values = await form.validateFields(fieldsToValidate);

            // 优先使用受控组件的时间值，如果没有则从表单获取
            const timeRangeFromState = timeRange;
            const timeRangeFromForm = values.timeRange;
            const finalTimeRange = timeRangeFromState || timeRangeFromForm;

            console.log('时间值来源检查:');
            console.log('受控组件时间值:', timeRangeFromState);
            console.log('表单时间值:', timeRangeFromForm);
            console.log('最终使用时间值:', finalTimeRange);

            const destinationWhsId = values.destinationWhsId === 'ALL' ? null : values.destinationWhsId;

            // 安全地处理时间转换
            let startTime: string | null = null;
            let endTime: string | null = null;

            if (finalTimeRange && Array.isArray(finalTimeRange) && finalTimeRange.length === 2) {
                try {
                    // 尝试不同的时间转换方式
                    if (finalTimeRange[0]) {
                        console.log('尝试转换开始时间...');
                        if (typeof finalTimeRange[0].toISOString === 'function') {
                            startTime = finalTimeRange[0].toISOString();
                            console.log('开始时间转换成功:', startTime);
                        } else if (typeof finalTimeRange[0].format === 'function') {
                            // 如果是 dayjs 对象，使用 toISOString()
                            startTime = finalTimeRange[0].toISOString();
                            console.log('开始时间 dayjs 转换成功:', startTime);
                        } else {
                            console.log('开始时间对象不支持 toISOString 方法');
                        }
                    }
                    if (finalTimeRange[1]) {
                        console.log('尝试转换结束时间...');
                        if (typeof finalTimeRange[1].toISOString === 'function') {
                            endTime = finalTimeRange[1].toISOString();
                            console.log('结束时间转换成功:', endTime);
                        } else if (typeof finalTimeRange[1].format === 'function') {
                            // 如果是 dayjs 对象，使用 toISOString()
                            endTime = finalTimeRange[1].toISOString();
                            console.log('结束时间 dayjs 转换成功:', endTime);
                        } else {
                            console.log('结束时间对象不支持 toISOString 方法');
                        }
                    }
                } catch (error) {
                    console.error('时间转换失败:', error);
                    if (error instanceof Error) {
                        console.error('错误堆栈:', error.stack);
                    }
                    message.warning('Time conversion failed, please select the time again.');
                }
            } else {
                console.log('finalTimeRange 不是有效的数组格式');
            }

            // 详细调试日志，帮助验证时间处理
            console.log('=== 时间选择器详细调试信息 ===');
            console.log('Form 表单原始值:', values);
            console.log('finalTimeRange 原始值:', finalTimeRange);
            console.log('finalTimeRange 类型:', typeof finalTimeRange);
            console.log('finalTimeRange 是否为数组:', Array.isArray(finalTimeRange));
            if (finalTimeRange) {
                console.log('finalTimeRange 长度:', finalTimeRange.length);
                console.log('finalTimeRange[0]:', finalTimeRange[0]);
                console.log('finalTimeRange[0] 类型:', typeof finalTimeRange[0]);
                console.log('finalTimeRange[0] 构造函数:', finalTimeRange[0]?.constructor?.name);
                console.log('finalTimeRange[0] 是否有 toISOString 方法:', typeof finalTimeRange[0]?.toISOString);
                console.log('finalTimeRange[1]:', finalTimeRange[1]);
                console.log('finalTimeRange[1] 类型:', typeof finalTimeRange[1]);
                console.log('finalTimeRange[1] 构造函数:', finalTimeRange[1]?.constructor?.name);
                console.log('finalTimeRange[1] 是否有 toISOString 方法:', typeof finalTimeRange[1]?.toISOString);
            }
            console.log('处理后的开始时间:', startTime);
            console.log('处理后的结束时间:', endTime);
            console.log('选择的包牌段数:', values.bagType);
            console.log('==============================');

            // 根据页面类型设置carrierCode
            const carrierCode = pageType === 'swiftx' ? 0 : values.carrierCode;

            console.log('页面类型:', pageType);
            console.log('values.carrierCode:', values.carrierCode);
            console.log('计算后的carrierCode:', carrierCode);
            console.log('完整的values对象:', values);

            // 对于other-carrier页面，必须选择Carrier
            if (pageType === 'other-carrier' && !values.carrierCode) {
                message.warning(t('wms.baggingTask.messages.pleaseSelectCarrierFirst'));
                return;
            }

            // 根据页面类型设置bagType
            const bagType = pageType === 'swiftx' ? values.bagType : BagType.ONE_SEGMENT;

            // 如果选择了ALL，则批量查询所有Carrier
            if (pageType === 'other-carrier' && values.carrierCode === 'ALL') {
                setSelectedCarrier('ALL');

                // 检查carrier数据是否已加载
                if (carrierLoading) {
                    message.warning(t('wms.baggingTask.messages.loadingCarriersPleaseWait'));
                    return;
                }

                // 获取所有实际的Carrier（排除ALL选项）
                const actualCarriers = carrierData?.getCarrierOptions || [];

                console.log('actualCarriers检查:', {
                    carrierData,
                    getCarrierOptions: carrierData?.getCarrierOptions,
                    actualCarriersLength: actualCarriers.length
                });

                if (actualCarriers.length === 0) {
                    message.warning(t('wms.baggingTask.messages.noCarriersAvailable'));
                    return;
                }

                console.log('批量查询所有Carrier:', actualCarriers);

                // 批量查询所有Carrier的预览数据
                const previewPromises = actualCarriers.map(carrier =>
                    client.query<GetConsolidationPreviewResponse, GetConsolidationPreviewVariables>({
                        query: GET_CONSOLIDATION_PREVIEW_STATISTICS,
                        variables: {
                            input: {
                                warehouseId: currentHubId,
                                destinationHubId: values.destinationHubId,
                                destinationWhsId: undefined, // Other Carrier页面没有WHS选择
                                expectedDeliveryTimeStart: startTime || undefined,
                                expectedDeliveryTimeEnd: endTime || undefined,
                                bagType: bagType,
                                carrierCode: carrier.value,
                            }
                        },
                        fetchPolicy: 'network-only'
                    }).then(result => ({
                        carrierCode: carrier.value,
                        data: result.data.getConsolidationPreviewStatistics
                    })).catch(error => {
                        console.error(`Carrier ${carrier.name} preview failed:`, error);
                        return {
                            carrierCode: carrier.value,
                            data: null
                        };
                    })
                );

                const results = await Promise.all(previewPromises);
                console.log('所有Carrier预览结果:', results);

                // 构建Carrier预览数据映射
                const newCarrierPreviewMap = new Map<number, GetConsolidationPreviewResponse['getConsolidationPreviewStatistics']>();
                let totalParcelCount = 0;
                let totalBagCount = 0;
                let hasValidData = false;

                results.forEach(result => {
                    if (result.data) {
                        newCarrierPreviewMap.set(result.carrierCode, result.data);
                        totalParcelCount += result.data.totalParcelCount || 0;
                        totalBagCount += result.data.estimatedBagCount || 0;
                        if (result.data.canCreateConsolidation) {
                            hasValidData = true;
                        }
                    }
                });

                setCarrierPreviewMap(newCarrierPreviewMap);

                // 设置汇总的预览数据
                setPreviewData({
                    totalParcelCount,
                    estimatedBagCount: totalBagCount,
                    routeStats: [], // 汇总时不需要详细的路由统计
                    canCreateConsolidation: hasValidData
                });

                console.log('汇总预览数据:', {
                    totalParcelCount,
                    estimatedBagCount: totalBagCount,
                    canCreateConsolidation: hasValidData
                });
            } else {
                // 单个Carrier的查询逻辑（原有逻辑）
                setSelectedCarrier(typeof carrierCode === 'number' ? carrierCode : 0);
                setCarrierPreviewMap(new Map()); // 清空批量数据
                
                loadPreview({
                    variables: {
                        input: {
                            warehouseId: currentWarehouse?.id!,
                            destinationHubId: values.destinationHubId,
                            destinationWhsId: destinationWhsId,
                            expectedDeliveryTimeStart: startTime || undefined,
                            expectedDeliveryTimeEnd: endTime || undefined,
                            bagType: bagType,
                            carrierCode: carrierCode,
                        }
                    }
                });
            }
        } catch (errorInfo) {
            // 检查具体的验证错误
            const errors = (errorInfo as any).errorFields || [];
            const missingFields = errors.map((error: any) => error.name[0]);

            if (missingFields.includes('bagType')) {
                message.warning(t('wms.baggingTask.messages.pleaseSelectTheBagType') || 'Please select the bag type');
            } else if (missingFields.includes('destinationHubId') || missingFields.includes('destinationWhsId')) {
                message.warning(t('wms.baggingTask.messages.pleaseSelectDestinationHubAndWHS') || 'Please select destination HUB and WHS');
            } else {
                message.warning(t('wms.baggingTask.messages.pleaseSelectDestinationHubAndWHS') || 'Please select destination HUB and WHS');
            }
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // 额外验证包类型（仅swiftx页面需要）
            if (pageType === 'swiftx' && !values.bagType) {
                message.warning(t('wms.baggingTask.messages.pleaseSelectTheBagType') || 'Please select the bag type');
                return;
            }

            if (!previewData?.canCreateConsolidation) {
                message.warning('当前条件无法创建组包，请检查预览结果,点击 update');
                return;
            }

            setLoading(true);
            
            // 安全地处理时间转换
            const timeRangeFromState = timeRange;
            const timeRangeFromForm = values.timeRange;
            const finalTimeRange = timeRangeFromState || timeRangeFromForm;
            
            let startTime: string | null = null;
            let endTime: string | null = null;
            
            if (finalTimeRange && Array.isArray(finalTimeRange) && finalTimeRange.length === 2) {
                try {
                    if (finalTimeRange[0] && typeof finalTimeRange[0].toISOString === 'function') {
                        startTime = finalTimeRange[0].toISOString();
                    }
                    if (finalTimeRange[1] && typeof finalTimeRange[1].toISOString === 'function') {
                        endTime = finalTimeRange[1].toISOString();
                    }
                } catch (error) {
                    console.error('时间转换失败:', error);
                    if (error instanceof Error) {
                        console.error('错误堆栈:', error.stack);
                    }
                    message.error('Time conversion failed, please select the time again.');
                    return;
                }
            }

            // 如果选择了ALL Carrier，需要批量创建任务
            if (pageType === 'other-carrier' && values.carrierCode === 'ALL') {
                // 从carrierPreviewMap中提取有值的Carrier任务
                const carrierTasks: Array<{
                    carrierCode: number;
                    estimatedParcelCount: number;
                    estimatedBagCount: number;
                }> = [];

                carrierPreviewMap.forEach((previewData, carrierCode) => {
                    if (previewData && previewData.canCreateConsolidation &&
                        (previewData.totalParcelCount > 0 || previewData.estimatedBagCount > 0)) {
                        carrierTasks.push({
                            carrierCode,
                            estimatedParcelCount: previewData.totalParcelCount,
                            estimatedBagCount: previewData.estimatedBagCount
                        });
                    }
                });

                if (carrierTasks.length === 0) {
                    message.warning(t('wms.baggingTask.messages.noValidCarriersToCreateTasks'));
                    setLoading(false);
                    return;
                }

                const taskData: CreateTaskData = {
                    destinationHubId: values.destinationHubId,
                    destinationWhsId: null,
                    startTime: startTime,
                    endTime: endTime,
                    estimatedParcelCount: previewData.totalParcelCount,
                    estimatedBagCount: previewData.estimatedBagCount,
                    bagType: BagType.ONE_SEGMENT,
                    isAllCarriers: true,
                    carrierTasks: carrierTasks
                };

                await onSubmit(taskData);
            } else {
                // 单个Carrier或SWIFTX的创建逻辑（原有逻辑）
                const taskData: CreateTaskData = {
                    destinationHubId: values.destinationHubId,
                    destinationWhsId: values.destinationWhsId === 'ALL' ? null : values.destinationWhsId,
                    startTime: startTime,
                    endTime: endTime,
                    estimatedParcelCount: previewData.totalParcelCount,
                    estimatedBagCount: previewData.estimatedBagCount,
                    bagType: pageType === 'swiftx' ? values.bagType : BagType.ONE_SEGMENT,
                    carrierCode: pageType === 'other-carrier' ? values.carrierCode : undefined,
                    isAllCarriers: false
                };

                await onSubmit(taskData);
            }

            onClose();
        } catch (error) {
            console.error('Form validation error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={
                <div className="flex items-center justify-between">
                    <span>{t('wms.baggingTask.buttons.newBaggingTask')}</span>
                </div>
            }
            placement="right"
            open={visible}
            onClose={onClose}
            width={620}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onClose}>{t('components.button.cancel')}</Button>
                    <Button 
                        type="primary" 
                        onClick={handleSubmit}
                        loading={loading || isExecuting}
                        disabled={loading || isExecuting}
                        className="bg-purple-500 hover:bg-purple-600 border-purple-500"
                    >
                        {isExecuting ? 'Executing...' : t('wms.baggingTask.buttons.createTask')}
                    </Button>
                </Space>
            }
        >
            <div
                className="space-y-6"
                style={{
                    paddingRight: '16px',
                    minWidth: '560px',
                    maxWidth: '560px',
                    overflow: 'hidden'
                }}
            >
                {/* 基本信息 */}
                <Form 
                    form={form}
                    layout="vertical"
                    initialValues={{
                        estimatedParcelCount: 0,
                        estimatedBagCount: 0
                    }}
                >
                    {/* Carrier选择（仅other-carrier页面显示） */}
                    {pageType === 'other-carrier' && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.columns.carrier')}</label>
                            <Form.Item
                                name="carrierCode"
                                rules={[{ required: true, message: t('wms.baggingTask.messages.pleaseSelectTheCarrier') || '' }]}
                                className="flex-1 mb-0"
                            >
                                <Select
                                    placeholder={t('wms.baggingTask.placeholders.carrier')}
                                    loading={carrierLoading}
                                    className="flex-1"
                                    onChange={(value) => {
                                        setSelectedCarrier(value);
                                        // 切换Carrier时清空预览数据
                                        setPreviewData(null);
                                        setCarrierPreviewMap(new Map());
                                    }}
                                >
                                    {carrierOptions.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">
                            {t(pageType === 'other-carrier' ? 'wms.baggingTask.filters.handoverHub' : 'wms.baggingTask.filters.destinationHub')}
                        </label>
                        <Form.Item
                            name="destinationHubId"
                            rules={[{ required: true, message: t('wms.baggingTask.messages.pleaseSelectTheDestinationHub') || '' }]}
                            className="flex-1 mb-0"
                        >
                            <Select 
                                placeholder={t('wms.baggingTask.placeholders.destinationHub') || ''}
                                loading={hubWhsLoading}
                                className="flex-1"
                                onChange={handleHubChange}
                            >
                                {hubOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    
                    {/* Destination WHS（仅swiftx页面显示） */}
                    {pageType === 'swiftx' && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.destinationWHS')}</label>
                            <Form.Item
                                name="destinationWhsId"
                                rules={[{ required: true, message: t('wms.baggingTask.messages.pleaseSelectTheDestinationWHS') || '' }]}
                                className="flex-1 mb-0"
                            >
                                <Select
                                    placeholder={t('wms.baggingTask.placeholders.destinationWHS') || ''}
                                    loading={hubWhsLoading}
                                    className="flex-1"
                                    disabled={!selectedHubId}
                                >
                                    {filteredWhsOptions.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.arrivalTime')}</label>
                        <Form.Item
                            name="timeRange"
                            rules={[{ required: false, message: ' ' }]}
                            className="flex-1 mb-0"
                        >
                            <DatePicker.RangePicker
                                showTime
                                format="YYYY/MM/DD HH:mm"
                                placeholder={[t('wms.baggingTask.placeholders.start') || '', t('wms.baggingTask.placeholders.end') || '']}
                                style={{ width: '100%' }}
                                value={timeRange}
                                onChange={(dates, dateStrings) => {
                                    // 调试日志：查看DatePicker原始返回值
                                    console.log('DatePicker onChange 触发:');
                                    console.log('dates:', dates);
                                    console.log('dates 类型:', typeof dates);
                                    console.log('dates 是否为数组:', Array.isArray(dates));
                                    console.log('dateStrings:', dateStrings);
                                    
                                    // 使用受控组件方式
                                    setTimeRange(dates);
                                    form.setFieldsValue({ timeRange: dates });
                                }}
                            />
                        </Form.Item>
                    </div>
                    
                    {/* Bag Type（仅swiftx页面显示） */}
                    {pageType === 'swiftx' && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.bagType')}</label>
                            <Form.Item
                                name="bagType"
                                rules={[{ required: true, message: t('wms.baggingTask.messages.pleaseSelectTheBagType') || '' }]}
                                className="flex-1 mb-0"
                            >
                                <Select
                                    placeholder={t('wms.baggingTask.placeholders.bagType') || ''}
                                    className="flex-1"
                                >
                                    {filteredBagTypeOptions.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.estParcelCount')}</label>
                        <InputNumber
                            value={previewData?.totalParcelCount || 0}
                            disabled
                            className="flex-1"
                        />
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20 ml-4">{t('wms.baggingTask.filters.estBagCount')}</label>
                        <InputNumber
                            value={previewData?.estimatedBagCount || 0}
                            disabled
                            className="flex-1"
                        />
                        <Button 
                            type="primary" 
                            onClick={handlePreview}
                            loading={previewLoading}
                            className="ml-auto bg-purple-500 hover:bg-purple-600 border-purple-500"
                            title="刷新预览统计"
                        >
                            {t('components.button.update')}
                        </Button>
                    </div>
                    

                </Form>
            </div>
        </Drawer>
    );
};

export default CreateConsolidationTaskDrawer; 